import { motion } from "framer-motion";
import {
  Info,
  Factory,
  Package,
  Truck,
  Briefcase,
  Download,
  Upload,
  MoreHorizontal,
  Check,
} from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "manufacturer", label: "Manufacturer", icon: Factory },
  { value: "wholesaler", label: "Wholesaler", icon: Package },
  { value: "distributor", label: "Distributor", icon: Truck },
  { value: "services", label: "Services", icon: Briefcase },
  { value: "importer", label: "Importer", icon: Download },
  { value: "exporter", label: "Exporter", icon: Upload },
  { value: "other", label: "Other (Specify)", icon: MoreHorizontal },
];

export default function Step4AdditionalInfo({ formData, setFormData, errors }) {
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryToggle = (categoryValue) => {
    setFormData((prev) => {
      const currentCategories = prev.selectedCategories || [];
      const isSelected = currentCategories.includes(categoryValue);
      if (isSelected) {
        const updatedCategories = currentCategories.filter((c) => c !== categoryValue);
        if (categoryValue === "other") {
          return { ...prev, selectedCategories: updatedCategories, otherCategorySpecification: "" };
        }
        return { ...prev, selectedCategories: updatedCategories };
      }
      return { ...prev, selectedCategories: [...currentCategories, categoryValue] };
    });
  };

  const isCategorySelected = (value) => (formData.selectedCategories || []).includes(value);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
          <Info size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Additional Information About your Company</h2>
      </div>

      <p className="mb-6 text-sm font-medium text-gray-700">
        Please tick the category which best describes the nature of your company
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CATEGORY_OPTIONS.map((option, index) => {
          const isSelected = isCategorySelected(option.value);
          const IconComponent = option.icon;
          return (
            <motion.div
              key={option.value}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
            >
              <button
                type="button"
                onClick={() => handleCategoryToggle(option.value)}
                className={`relative w-full p-4 text-left bg-white rounded-lg border-2 transition-all duration-300 cursor-pointer group ${
                  isSelected
                    ? "text-white bg-gradient-to-br from-purple-500 to-purple-600 border-purple-500 shadow-lg"
                    : "border-gray-200 hover:border-purple-300 hover:shadow-md"
                }`}
              >
                {isSelected && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-2 right-2">
                    <div className="flex justify-center items-center w-5 h-5 text-white rounded-full backdrop-blur-sm bg-white/20">
                      <Check size={12} />
                    </div>
                  </motion.div>
                )}
                <div
                  className={`flex justify-center items-center w-10 h-10 mb-3 rounded-lg transition-all ${
                    isSelected ? "backdrop-blur-sm bg-white/20" : "bg-purple-50 group-hover:scale-110"
                  }`}
                >
                  <IconComponent size={20} className={isSelected ? "text-white" : "text-purple-600"} />
                </div>
                <h4 className={`text-sm font-semibold leading-tight ${isSelected ? "text-white" : "text-gray-900"}`}>
                  {option.label}
                </h4>
              </button>
            </motion.div>
          );
        })}
      </div>

      {isCategorySelected("other") && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <label className="block mb-2 text-sm font-semibold text-gray-700">
            Please specify your other category <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.otherCategorySpecification || ""}
            onChange={(e) => handleChange("otherCategorySpecification", e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 ${
              errors?.otherCategorySpecification ? "border-red-300 bg-red-50" : "border-gray-300"
            }`}
            placeholder="Please specify your other category..."
          />
          {errors?.otherCategorySpecification && <p className="mt-1 text-sm text-red-500">{errors.otherCategorySpecification}</p>}
        </motion.div>
      )}
      {errors?.selectedCategories && <p className="mt-4 text-sm text-red-500">{errors.selectedCategories}</p>}
    </motion.div>
  );
}
